import React from 'react';

import {
	AtlassianIcon,
	BitbucketIcon,
	ConfluenceIcon,
	GoalsIcon,
	HomeIcon,
	JiraIcon,
	type LogoProps,
	LoomIcon,
	ProjectsIcon,
	TeamsIcon,
} from '@atlaskit/logo';
import { IconTile } from '@atlaskit/icon';
import { Flex } from '@atlaskit/primitives/compiled';

import type { Source } from './PopularQuestions.tsx';

const iconOverrides: Record<string, React.ElementType<LogoProps>> = {
	Confluence: ConfluenceIcon,
	Jira: JiraIcon,
	Loom: LoomIcon,
	Bitbucket: BitbucketIcon,
	Goals: GoalsIcon,
	Projects: ProjectsIcon,
	Teams: TeamsIcon,
	Home: HomeIcon,
};

const AtlassianUnknownIcon = () => <AtlassianIcon label="" size="xxsmall" />;

type AppIconProps = {
	url?: string | null;
	completeKey?: string | null;
};

export const getSourceIcon = ({ url, completeKey }: AppIconProps) => {
	if (completeKey) {
		const IconOverride = iconOverrides[completeKey];
		if (IconOverride) {
			return <IconOverride size="xxsmall" shouldUseNewLogoDesign />;
		}
	}

	if (!url) {
		return (
			<IconTile icon={AtlassianUnknownIcon} label="" appearance="gray" size="16" shape="square" />
		);
	}

	return (
		<svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true" data-testid="app-icon">
			<defs>
				<filter id="changeFillColor">
					<feColorMatrix
						type="matrix"
						values="0.02, 0, 0, 0, 0, 0, 0.04, 0, 0, 0, 0, 0, 0.07, 0, 0, 0, 0, 0, 1, 0"
					/>
				</filter>
			</defs>
			{/* eslint-disable-next-line no-restricted-syntax */}
			<image
				xmlnsXlink="http://www.w3.org/1999/xlink"
				xlinkHref={url}
				width="16"
				height="16"
				x="0"
				y="0"
				filter={isSvg(url) ? 'url(#changeFillColor)' : ''}
			/>
		</svg>
	);
};

export const getSourceIconStack = (sources: readonly Source[]) => {
	const uniqueIconUrls = Array.from(
		new Set(sources.map((source) => source?.content?.embeddedProduct)),
	);
	return (
		<Flex>
			{uniqueIconUrls.map((product) => (
				<>{getSourceIcon({ url: undefined, completeKey: product })}</>
			))}
		</Flex>
	);
};

const isSvg = (url: string) => {
	const fileType = url.split(/\#|\?/)[0].split('.').pop();
	return !!fileType && fileType.trim() === 'svg';
};
